/**
 * Sitewide breadcrumb registry.
 *
 * One source of truth for BOTH the visible <Breadcrumbs /> trail and the
 * schema.org BreadcrumbList emitted by <PageMeta />. Google only shows
 * breadcrumb rich results when the markup matches a real, resolvable trail, so
 * every href here must correspond to an actual route in src/App.tsx — that
 * invariant is enforced by src/test/breadcrumb-structured-data.test.tsx.
 */

import { SITE_URL } from "@/lib/entity-jsonld";

export interface BreadcrumbCrumb {
  name: string;
  /** Route path. Omitted on the current (last) crumb. */
  href?: string;
}

const HOME: BreadcrumbCrumb = { name: "Home", href: "/" };

/**
 * Parent trail for each indexable route, keyed by canonical path.
 * The current page's own crumb is appended automatically from `label`.
 */
interface TrailSpec {
  /** Label for the current page (last crumb — no link). */
  label: string;
  /** Ancestors between Home and the current page. */
  parents?: BreadcrumbCrumb[];
}

export const BREADCRUMB_TRAILS: Record<string, TrailSpec> = {
  "/about": { label: "About Dr. Sully" },
  "/speaking": { label: "Speaking" },
  "/framework": { label: "MAKECHANGE Framework" },
  "/scan": { label: "Free Scan" },
  "/books": { label: "Books" },
  "/ebooks": { label: "eBooks", parents: [{ name: "Books", href: "/books" }] },
  "/blog": { label: "Blog" },
  "/stories": { label: "Stories" },
  "/reviews": { label: "Reviews" },
  "/invest": { label: "Invest" },
  "/masterclass": {
    label: "Built On Purpose Masterclass",
    parents: [{ name: "Invest", href: "/invest" }],
  },
  "/book": { label: "Book a Session", parents: [{ name: "Invest", href: "/invest" }] },
  "/strategy-session": { label: "Strategy Session" },
  "/inquire": { label: "Inquire" },
  "/advocacy": { label: "Full-Scope Advocacy Partner" },
  "/advocacy/pricing": {
    label: "Pricing",
    parents: [{ name: "Advocacy", href: "/advocacy" }],
  },
  "/advocacy/how-it-works": {
    label: "How It Works",
    parents: [{ name: "Advocacy", href: "/advocacy" }],
  },
  "/advocacy/intake": {
    label: "Intake",
    parents: [{ name: "Advocacy", href: "/advocacy" }],
  },
  "/advocacy/compassion-apply": {
    label: "Compassion Application",
    parents: [{ name: "Advocacy", href: "/advocacy" }],
  },
  "/honest-kingdom-voice": { label: "Honest Kingdom Voice" },
  "/share-your-story": { label: "Share Your Story", parents: [{ name: "Stories", href: "/stories" }] },
  "/free": { label: "Free Resources" },
  "/links": { label: "Links" },
  "/legal": { label: "Legal" },
};

/** Normalize an incoming path the same way PageMeta canonicalizes it. */
export const normalizeBreadcrumbPath = (path: string) => {
  const clean = path.split("?")[0].split("#")[0].toLowerCase();
  const withSlash = clean.startsWith("/") ? clean : `/${clean}`;
  return withSlash.length > 1 && withSlash.endsWith("/")
    ? withSlash.slice(0, -1)
    : withSlash;
};

/** Full crumb trail (Home → … → current) for a route, or null when unmapped. */
export const breadcrumbTrail = (path: string): BreadcrumbCrumb[] | null => {
  const key = normalizeBreadcrumbPath(path);
  const spec = BREADCRUMB_TRAILS[key];
  if (!spec) return null;
  return [HOME, ...(spec.parents ?? []), { name: spec.label }];
};

const absolute = (href: string) =>
  href === "/" ? `${SITE_URL}/` : `${SITE_URL}${href}`;

/**
 * schema.org BreadcrumbList node for a route. The last item intentionally has
 * no `item` URL (it is the current page) per Google's guidance.
 */
export const breadcrumbJsonLd = (path: string): Record<string, unknown> | null => {
  const trail = breadcrumbTrail(path);
  if (!trail || trail.length < 2) return null;
  return {
    "@type": "BreadcrumbList",
    "@id": `${absolute(normalizeBreadcrumbPath(path))}#breadcrumb`,
    itemListElement: trail.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      ...(crumb.href ? { item: absolute(crumb.href) } : {}),
    })),
  };
};
